/**
 * Token key lists shared by the theme engine and panda.config.ts
 *
 * Keep this file free of runtime imports so Panda can load it.
 */

/**
 * Material colour roles exposed as `--typ-color-*` (hex) and `--typ-rgb-*` (R, G, B)
 */
export const COLOUR_ROLES = [
  "primary",
  "on-primary",
  "primary-container",
  "on-primary-container",
  "secondary",
  "on-secondary",
  "secondary-container",
  "on-secondary-container",
  "tertiary",
  "on-tertiary",
  "tertiary-container",
  "on-tertiary-container",
  "error",
  "on-error",
  "error-container",
  "on-error-container",
  "primary-fixed",
  "primary-fixed-dim",
  "on-primary-fixed",
  "on-primary-fixed-variant",
  "secondary-fixed",
  "secondary-fixed-dim",
  "on-secondary-fixed",
  "on-secondary-fixed-variant",
  "tertiary-fixed",
  "tertiary-fixed-dim",
  "on-tertiary-fixed",
  "on-tertiary-fixed-variant",
  "surface-dim",
  "surface",
  "surface-bright",
  "surface-container-lowest",
  "surface-container-low",
  "surface-container",
  "surface-container-high",
  "surface-container-highest",
  "on-surface",
  "on-surface-variant",
  "outline",
  "outline-variant",
  "inverse-surface",
  "inverse-on-surface",
  "inverse-primary",
  "scrim",
  "shadow",
] as const;

export type ColourRole = (typeof COLOUR_ROLES)[number];

/**
 * Colour roles taken from the neutral palette
 */
export const NEUTRAL_ROLES: ColourRole[] = [
  "surface-dim",
  "surface",
  "surface-bright",
  "surface-container-lowest",
  "surface-container-low",
  "surface-container",
  "surface-container-high",
  "surface-container-highest",
  "on-surface",
  "inverse-surface",
  "inverse-on-surface",
];

/**
 * Colour roles taken from the neutral variant palette
 */
export const NEUTRAL_VARIANT_ROLES: ColourRole[] = [
  "on-surface-variant",
  "outline",
  "outline-variant",
];

/**
 * Presence indicator colours
 */
export const PRESENCE_KEYS = [
  "online",
  "idle",
  "busy",
  "focus",
  "invisible",
] as const;

export type PresenceKey = (typeof PRESENCE_KEYS)[number];

/**
 * Corner radius scale (Material 3 Expressive)
 */
export const RADIUS_KEYS = [
  "none",
  "xs",
  "sm",
  "md",
  "lg",
  "li",
  "xl",
  "xli",
  "xxl",
  "full",
  "circle",
] as const;

export type RadiusKey = (typeof RADIUS_KEYS)[number];

/**
 * Spacing scale
 */
export const SPACE_KEYS = [
  "none",
  "xxs",
  "xs",
  "s",
  "sm",
  "md",
  "l",
  "lg",
  "x",
  "xl",
  "xxl",
] as const;

export type SpaceKey = (typeof SPACE_KEYS)[number];

/**
 * Elevation levels
 */
export const SHADOW_KEYS = ["0", "1", "2", "3", "4"] as const;

export type ShadowKey = (typeof SHADOW_KEYS)[number];

/**
 * Fixed layout dimensions
 */
export const LAYOUT_KEYS = [
  "width-channel-sidebar",
  "width-user-context-menu-truncate",
  "height-message-box",
] as const;

export type LayoutKey = (typeof LAYOUT_KEYS)[number];
